import React, { Component, MouseEventHandler } from 'react';
import { bisectLeft, max, min } from 'd3-array';
import { ScaleLinear, scaleLinear } from 'd3-scale';
import { throttle } from 'lodash-es';

import { color } from '@strava/ui-tokens/js';

import { Area } from './components/Area';
import { Axis } from './components/Axis';
import { SurfaceTypePath } from './components/SurfaceTypePath';
import { Tooltip } from './components/Tooltip';

import styles from './ElevationChart.module.scss';

type Units = 'metric' | 'imperial';
type ElevationSteam = number[];
type DistanceSteam = number[];
type SurfaceSteam = number[] | string[] | null;

export interface Formatters {
  elevationFormatter: {
    convert: (meters: number) => number;
    formatShort: (meters: number) => string;
  };
  distanceFormatter: {
    convert: (meters: number) => number;
    formatShort: (meters: number, decimal?: number) => string;
  };
  gradePercentFormatter: {
    formatShort: (meters: number) => string;
  };
}

export interface ElevationChartProps {
  height: number;
  elevation: ElevationSteam;
  distance: DistanceSteam;
  surface?: SurfaceSteam;
  units: Units;
  margin: {
    top: number;
    right: number;
    bottom: number;
    left: number;
  };
  scopeTo?: [number, number] | null;
  scopeColor?: string;
  areaColor?: string;
  highlightArea?: number[] | null;
  highlightAreaColor?: string;
  onUpdateMarker?: (
    index: number | null,
    { distance }: { distance: number | null }
  ) => void;
  onMouseLeave?: MouseEventHandler<SVGRectElement>;
  hoveredDistance?: number | null;
  formatters: {
    ElevationFormatter: (units: Units) => Formatters['elevationFormatter'];
    DistanceFormatter: (units: Units) => Formatters['distanceFormatter'];
    PercentFormatter: () => Formatters['gradePercentFormatter'];
  };
  tooltipDistanceLabel?: string;
  tooltipElevationLabel?: string;
  tooltipGradeLabel?: string;
  tooltipSurfaceLabel?: string;
  tooltipSurfaceTypes?: {
    paved: string;
    unpaved: string;
    unknown: string;
  };
  className?: string;
}

interface ElevationChartState {
  stream: {
    elevation: ElevationSteam;
    convertedElevation: ElevationSteam;
    distance: DistanceSteam;
    convertedDistance: DistanceSteam;
    surface?: SurfaceSteam;
  } | null;
  xScale: ScaleLinear<number, number, never> | null;
  yScale: ScaleLinear<number, number, never> | null;
  height: number;
  width: number;
  markerPosition: number | null;
  tooltipContent: {
    distance: { label: string; value: string };
    elevation: { label: string; value: string };
    grade: { label: string; value: string };
    surface?: { label: string; value: string };
  } | null;
}

export class ElevationChart extends Component<
  ElevationChartProps,
  ElevationChartState
> {
  static defaultProps = {
    margin: {
      top: 10,
      right: 24,
      bottom: 20,
      left: 45
    },
    stream: {
      elevation: [],
      distance: [],
      surface: null
    },
    surface: null,
    scopeTo: null,
    scopeColor: 'rgb(23, 90, 189)',
    areaColor: 'rgba(100, 100, 100, 0.66)',
    highlightArea: null,
    highlightAreaColor: color.colorCoreO3,
    hoveredDistance: null,
    tooltipDistanceLabel: 'Distance',
    tooltipElevationLabel: 'Elevation',
    tooltipGradeLabel: 'Grade',
    tooltipSurfaceLabel: 'Surface Type',
    tooltipSurfaceTypes: {
      paved: 'Paved',
      unpaved: 'Unpaved',
      unknown: 'Unknown'
    }
  };

  state: ElevationChartState = {
    stream: null,
    xScale: null,
    yScale: null,
    height: 0,
    width: 0,
    markerPosition: null,
    tooltipContent: null
  };

  container = React.createRef<HTMLDivElement>();

  node = React.createRef<SVGSVGElement>();

  hitbox = React.createRef<SVGRectElement>();

  formatters: Formatters | null = null;

  handleResize = throttle(() => {
    this.buildScales();
  }, 250);

  componentDidMount() {
    window.addEventListener('resize', this.handleResize);
    this.updateFormatters();
    this.buildScales();
  }

  componentDidUpdate({
    elevation: prevElevation,
    distance: prevDistance,
    surface: prevSurface,
    units: prevUnits,
    scopeTo: prevScopeTo,
    hoveredDistance: prevHoveredDistance
  }: Readonly<ElevationChartProps>) {
    const { distance, elevation, units, hoveredDistance, scopeTo, surface } =
      this.props;
    const elevationChanged = prevElevation.length !== elevation.length;
    const distanceChanged = prevDistance.length !== distance.length;
    const surfaceChanged = prevSurface?.length !== surface?.length;
    const unitsChanged = prevUnits !== units;
    const hasScopeChanged =
      prevScopeTo?.[0] !== scopeTo?.[0] || prevScopeTo?.[1] !== scopeTo?.[1];

    if (unitsChanged) {
      this.updateFormatters();
    }

    if (elevationChanged || distanceChanged || unitsChanged || surfaceChanged) {
      this.buildScales();
    }

    if (hasScopeChanged) {
      this.scopeTo(scopeTo);
    }

    if (prevHoveredDistance !== hoveredDistance) {
      if (hoveredDistance === null || hoveredDistance === undefined) {
        // eslint-disable-next-line react/no-did-update-set-state
        this.setState({ markerPosition: null, tooltipContent: null });
      } else {
        const streamIndex = this.getStreamIndex(hoveredDistance);
        if (streamIndex !== -1) {
          this.updateTooltipContent(streamIndex, hoveredDistance);
        }
      }
    }
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.handleResize);
  }

  getStreamIndex = (distance: number) => {
    const { stream } = this.state;
    if (!stream || !stream.distance) {
      return -1;
    }

    return Math.min(
      bisectLeft(stream.distance, distance),
      stream.distance.length - 1
    );
  };

  // TODO: hover got really slow on long streams
  updateMarker: MouseEventHandler<SVGRectElement> = (e) => {
    const { onUpdateMarker } = this.props;
    const { xScale } = this.state;
    const distanceFormatter = this.formatters?.distanceFormatter;
    const bounds = this.hitbox.current?.getBoundingClientRect();
    if (bounds === undefined || distanceFormatter === undefined) {
      return;
    }
    const cursorPosition = e.clientX - bounds.left;
    const distCursor = xScale?.invert(cursorPosition);
    if (distCursor === undefined) {
      return;
    }
    // distCursor at this point will be in kilometers or miles so convert distance back to meters
    // as stream.distance is in meters so we want to be consistent
    const distance = distCursor / distanceFormatter.convert(1);
    const streamIndex = this.getStreamIndex(distance);

    if (streamIndex === -1 || Number.isNaN(distance)) {
      return;
    }

    onUpdateMarker?.(streamIndex, { distance });
    this.updateTooltipContent(streamIndex, distance);
  };

  updateTooltipContent = (streamIndex: number, distance: number) => {
    const {
      tooltipDistanceLabel,
      tooltipElevationLabel,
      tooltipGradeLabel,
      tooltipSurfaceLabel,
      tooltipSurfaceTypes
    } = this.props;
    const { stream, xScale } = this.state;
    const elevationFormatter = this.formatters?.elevationFormatter;
    const distanceFormatter = this.formatters?.distanceFormatter;
    const gradePercentFormatter = this.formatters?.gradePercentFormatter;

    if (
      xScale === null ||
      elevationFormatter === undefined ||
      distanceFormatter === undefined ||
      gradePercentFormatter === undefined ||
      tooltipDistanceLabel === undefined ||
      tooltipElevationLabel === undefined ||
      tooltipGradeLabel === undefined ||
      tooltipSurfaceLabel === undefined
    ) {
      return;
    }

    const distanceValue = stream?.distance[streamIndex] ?? 0;
    const elevationValue = stream?.elevation[streamIndex] ?? 0;
    const gradeValue = this.gradeAtIndex(streamIndex);
    const surfaceValue = stream?.surface?.[streamIndex] ?? null;

    const tooltipContent: {
      distance: { label: string; value: string };
      elevation: { label: string; value: string };
      grade: { label: string; value: string };
      surface?: { label: string; value: string };
    } = {
      distance: {
        label: tooltipDistanceLabel,
        value: distanceFormatter.formatShort(distanceValue, 2)
      },
      elevation: {
        label: tooltipElevationLabel,
        value: elevationFormatter.formatShort(elevationValue)
      },
      grade: {
        label: tooltipGradeLabel,
        value: gradePercentFormatter.formatShort(gradeValue)
      }
    };

    if (surfaceValue && tooltipSurfaceTypes) {
      let surfaceTypeValue;
      if ([1, 'Paved'].includes(surfaceValue)) {
        surfaceTypeValue = tooltipSurfaceTypes.paved;
      } else if ([2, 'Unpaved'].includes(surfaceValue)) {
        surfaceTypeValue = tooltipSurfaceTypes.unpaved;
      } else {
        surfaceTypeValue = tooltipSurfaceTypes.unknown;
      }

      tooltipContent.surface = {
        label: tooltipSurfaceLabel,
        value: surfaceTypeValue
      };
    }

    this.setState({
      // distance at this point will be in meeters or feet and we need to convert it to
      // kilometers or miles so that we can get the x position of the marker
      markerPosition: xScale(distance * distanceFormatter.convert(1)),
      tooltipContent
    });
  };

  // MouseEventHandler<HTMLDivElement>
  handleMouseLeave: MouseEventHandler<SVGRectElement> = (e) => {
    const { onUpdateMarker, onMouseLeave } = this.props;
    onUpdateMarker?.(null, { distance: null });
    onMouseLeave?.(e);
    this.setState({ markerPosition: null, tooltipContent: null });
  };

  updateFormatters() {
    const { units, formatters } = this.props;

    this.formatters = {
      // @ts-ignore - we need to redo how we handle this formatters from scratch, so
      // in order to not block TS migration, we disable the error for now
      elevationFormatter: new formatters.ElevationFormatter(units),
      // @ts-ignore - we need to redo how we handle this formatters from scratch, so
      // in order to not block TS migration, we disable the error for now
      distanceFormatter: new formatters.DistanceFormatter(units),
      // @ts-ignore - we need to redo how we handle this formatters from scratch, so
      // in order to not block TS migration, we disable the error for now
      gradePercentFormatter: new formatters.PercentFormatter()
    };
  }

  buildScales() {
    const {
      margin,
      height: heightProp,
      elevation,
      distance,
      surface
    } = this.props;
    const elevationFormatter = this.formatters?.elevationFormatter;
    const distanceFormatter = this.formatters?.distanceFormatter;

    if (elevationFormatter === undefined || distanceFormatter === undefined) {
      return;
    }

    const elevationOrDefault = elevation.length === 0 ? [0, 100] : elevation;

    const convertedElevation = elevationOrDefault.map((elev) =>
      elevationFormatter.convert(elev)
    );
    const convertedDistance = distance.map((dist) =>
      distanceFormatter.convert(dist)
    );

    const width =
      (this.container.current?.offsetWidth ?? 0) - margin.left - margin.right;
    const height = heightProp - margin.top - margin.bottom;
    const yMax = max(convertedElevation) ?? 0;
    const yMin = min(convertedElevation) ?? 0;
    const xScale = scaleLinear().range([0, width]);
    const yScale = scaleLinear().range([height, 0]);
    const yRange = yMax - yMin;
    const yPadding = yRange * 0.02;

    const xDomainMin = min(convertedDistance);
    const xDomainMax = max(convertedDistance);

    xScale.domain([xDomainMin ?? 0, xDomainMax ?? 0]);
    yScale.domain([yMin - yPadding, yMax + yPadding]);

    this.setState({
      height,
      width,
      xScale,
      yScale,
      stream: {
        elevation: elevationOrDefault,
        convertedElevation,
        distance,
        convertedDistance,
        surface
      }
    });
  }

  gradeAtIndex(index: number) {
    // Taken from charts.js: 1689 but extending the window to 5 data points
    const { stream } = this.state;

    if (stream === null) {
      return 0;
    }
    const { elevation, distance } = stream;
    const maxIndex = distance.length - 1;
    const threshold = 2;

    let streamIndex = index;

    if (streamIndex < threshold) {
      streamIndex = threshold;
    }
    if (maxIndex - streamIndex < threshold) {
      streamIndex = maxIndex - threshold;
    }

    const prevDistance = distance[streamIndex - threshold];
    const nextDistance = distance[streamIndex + threshold];
    const prevElevation = elevation[streamIndex - threshold];
    const nextElevation = elevation[streamIndex + threshold];
    if (
      prevDistance === undefined ||
      nextDistance === undefined ||
      prevElevation === undefined ||
      nextElevation === undefined
    ) {
      return 0;
    }

    const distanceDiff = nextDistance - prevDistance;
    const elevationDiff = nextElevation - prevElevation;
    const grade = (elevationDiff * 100) / distanceDiff;

    return grade;
  }

  scopeTo(scopeTo: [number, number] | null | undefined) {
    const { stream, xScale, yScale } = this.state;

    if (stream === null || xScale === null || yScale === null) {
      return;
    }

    const start = scopeTo ? scopeTo[0] : 0;
    const end = scopeTo ? scopeTo[1] : stream.distance.length - 1;

    // Taken from directory/ElevationChart.js.coffee: 263
    const range = end - start;
    // the padding on each side of the selected area should ideally be
    // 1/3 of the area itself, but this can't be the case if the start
    // or the end are too close to the edges
    const padding = Math.floor(range / 3);
    const enoughSpaceAtStart = start - padding <= 0;
    const enoughSpaceAtEnd = end + padding > stream.distance.length - 1;

    const paddedStart = enoughSpaceAtStart
      ? Math.floor(start / 3)
      : start - padding;

    const paddedEnd = enoughSpaceAtEnd
      ? Math.floor(end + (stream.distance.length - 1 - end) / 3)
      : end + padding;

    if (
      stream.convertedDistance[paddedStart] === undefined ||
      stream.convertedDistance[paddedEnd] === undefined
    ) {
      return;
    }

    const newXScale = scaleLinear()
      .range(xScale.range())
      .domain([
        stream.convertedDistance[paddedStart],
        stream.convertedDistance[paddedEnd]
      ]);

    const croppedElevationStream = stream.convertedElevation.slice(
      paddedStart,
      paddedEnd + 1
    );
    const yMax = max(croppedElevationStream) ?? 0;
    const yMin = min(croppedElevationStream) ?? 0;
    const yRange = yMax - yMin;
    const yPadding = yRange * 0.02;
    const newYScale = scaleLinear()
      .range(yScale.range())
      .domain([yMin - yPadding, yMax + yPadding]);

    this.setState({ xScale: newXScale, yScale: newYScale });
  }

  render() {
    const {
      margin,
      scopeTo,
      scopeColor,
      areaColor,
      highlightArea,
      highlightAreaColor,
      className
    } = this.props;
    const {
      stream,
      xScale,
      yScale,
      width,
      height,
      markerPosition,
      tooltipContent
    } = this.state;

    const streamDistanceLength = stream?.distance.length ?? 0;

    return (
      <div className={className} ref={this.container}>
        {height > 0 && width > 0 && (
          <svg
            ref={this.node}
            width={width + margin.left + margin.right}
            height={height + margin.top + margin.bottom}
          >
            <g transform={`translate(${margin.left}, ${margin.top})`}>
              {xScale && yScale && this.formatters && (
                <Axis
                  xScale={xScale}
                  yScale={yScale}
                  height={height}
                  width={width}
                  formatters={this.formatters}
                  scopeTo={scopeTo}
                />
              )}

              {xScale && yScale && stream && streamDistanceLength > 0 && (
                <>
                  <Area
                    fill={areaColor}
                    xScale={xScale}
                    yScale={yScale}
                    height={height}
                    stream={stream}
                    clipPath="url(#clipArea)"
                  />

                  {stream?.surface && (
                    <SurfaceTypePath
                      xScale={xScale}
                      yScale={yScale}
                      stream={stream}
                      clipPath="url(#clipArea)"
                    />
                  )}
                </>
              )}

              {xScale && yScale && stream && scopeTo && (
                <Area
                  fill={scopeColor}
                  xScale={xScale}
                  yScale={yScale}
                  height={height}
                  stream={{
                    convertedElevation: stream.convertedElevation.slice(
                      scopeTo[0],
                      scopeTo[1]
                    ),
                    convertedDistance: stream.convertedDistance.slice(
                      scopeTo[0],
                      scopeTo[1]
                    )
                  }}
                />
              )}

              {xScale && yScale && stream && highlightArea && (
                <Area
                  fill={highlightAreaColor}
                  xScale={xScale}
                  yScale={yScale}
                  height={height}
                  stream={{
                    convertedElevation: stream.convertedElevation.slice(
                      highlightArea[0],
                      highlightArea[1]
                    ),
                    convertedDistance: stream.convertedDistance.slice(
                      highlightArea[0],
                      highlightArea[1]
                    )
                  }}
                />
              )}

              <clipPath id="clipArea">
                <rect
                  x={0}
                  y={-margin.top}
                  height={height + margin.top}
                  width={width}
                />
              </clipPath>

              <g>
                <rect
                  className={styles.hitbox}
                  ref={this.hitbox}
                  x={0}
                  y={-margin.top}
                  height={height + margin.top}
                  width={width}
                  onMouseMove={this.updateMarker}
                  onMouseLeave={this.handleMouseLeave}
                />
                {streamDistanceLength > 0 &&
                  markerPosition &&
                  tooltipContent && (
                    <Tooltip
                      position={markerPosition}
                      content={tooltipContent}
                      clipPath="url(#clipArea)"
                      chartWidth={width}
                      chartMargin={margin}
                    />
                  )}
              </g>
            </g>
          </svg>
        )}
      </div>
    );
  }
}
